import { useEffect, useState } from "react";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * Self-service profile page. Every logged-in user can edit their own photo
 * and one-line bio here. Family-notification emails use these fields when
 * this user is the assigned carer.
 */
export default function MyProfile() {
  const { user } = useAuth();
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get(`/auth/me`);
        if (!cancelled) setBio(data.user?.bio || "");
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="my-profile-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">My profile</h1>
      <p className="text-sm text-slate-600 mb-6">Your photo and bio appear in the courtesy email that families receive when you&apos;re assigned to a visit. Make it warm and reassuring.</p>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-6">
          <UserAvatar userId={user.id} name={user.full_name} size={96} editable={true} bio={bio} onSaved={() => window.location.reload()} />
          <div>
            <p className="font-semibold text-lg text-slate-900">{user.full_name}</p>
            <p className="text-sm text-slate-500 capitalize">{(user.role || "").replace(/_/g, " ")}</p>
            <p className="text-sm text-slate-500">{user.email}</p>
          </div>
        </div>
        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="mt-6 border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Bio</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap min-h-[24px]" data-testid="my-profile-bio-display">{bio || "No bio yet — click the camera icon on your photo to add one."}</p>
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
        <p className="font-semibold text-slate-700 mb-1">Tips for a good profile</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Use a clear head-and-shoulders photo — helps families recognise you at the door.</li>
          <li>Bio: mention experience areas (e.g. dementia, palliative care), any specialist training, and something warm.</li>
          <li>No personal contact details in the bio — use the office.</li>
        </ul>
      </div>
    </div>
  );
}
